import Styles from './MobileSelectImage.module.scss'
import { useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { AI, LinkAngled, Upload5 } from 'blaze-ui/icons/30'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { useStateRef } from 'hooks/useStateRef'
import { Key, MutableRefObject, useEffect, useState } from 'react'
import { useField } from 'formik'
import { StockImages } from './StockImages'
import { LibraryImages } from './LibraryImages'
import { ConfirmImage } from './ConfirmImage'
import { SelectedImage } from 'types'
import { SegmentedControl } from 'blaze-ui/SegmentedControl/SegmentedControl'
import { Text } from 'blaze-ui/Text/Text'
import { ChevronRight } from 'blaze-ui/icons/24'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { ImagePickerDrawer } from 'components/ImagePickerModal/ImagePickerDrawer/ImagePickerDrawer'
import { Provider, Providers } from 'components/ImagePickerModal/Types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { BrandKitImages } from './BrandKitImages'
import { Item } from '@react-stately/collections'

interface Props {
  resolveRef: MutableRefObject<((value: boolean | PromiseLike<boolean>) => void) | undefined>
  onSubmit: (values: any) => Promise<any>
}

export const MobileSelectImage = observer(({ resolveRef, onSubmit }: Props) => {
  const { drawersStore } = useRootStore()
  const [fileInputRef, setFileInputRef] = useStateRef<HTMLInputElement | null>(null)
  const [view, setView] = useState<Key>('stock')
  const [showConfirmImage, setShowConfirmImage] = useState<boolean>()
  const imagePickerContext = useImagePickerContext()
  const [, , { setValue: setMediaFileId }] = useField('initial_media_file_id')
  const { value: brandKitId } = useField<number>('brand_kit_id')[0]
  const { selectedImages, onUploadStart, uploadImages, accept, toggleSelectedImage } = imagePickerContext

  const onSelectImage = (image: SelectedImage) => {
    toggleSelectedImage(image)
    setShowConfirmImage(true)
  }

  const handleOnChange = async (files: File[]) => {
    const toast = showToastMessage('Uploading...', 'loading', undefined, 0)
    await onUploadStart?.(files)
    const uploadedImages = await uploadImages(files, imagePickerContext)
    toast()
    onSelectImage(uploadedImages[0])
  }

  const onBack = () => {
    resolveRef.current?.(false)
    toggleSelectedImage(selectedImages[0])
    setShowConfirmImage(false)
  }

  useEffect(() => {
    setMediaFileId(selectedImages?.[0]?.id || selectedImages?.[0]?.unsplash?.id)
  }, [selectedImages]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = (provider: Provider, props?: { brandKitId?: number }) => {
    drawersStore.openDrawer(ImagePickerDrawer, {
      ...imagePickerContext,
      hiddenProviders: new Set<Provider>(Object.values(Providers).filter((p) => p !== provider) as Provider[]),
      defaultProvider: provider,
      onBeforeConfirm: async (images: SelectedImage[]) => {
        onSelectImage(images[0])
        drawersStore.closeDrawer()

        return new Promise((resolve) => {
          resolveRef.current = resolve
        })
      },
      onConfirm: async (images) => {
        onSubmit({
          initial_media_file_id: images[0].id,
        })
      },
      onCancel: () => {
        drawersStore.closeDrawer()
      },
      ...props,
    })
  }

  if (showConfirmImage)
    return <ConfirmImage imageUrl={selectedImages[0].preview_url || selectedImages[0].url} onBack={onBack} />

  const segmentedItems = brandKitId
    ? [
        { name: 'Stock Photos', id: 'stock' },
        { name: 'Brand Kit', id: 'brandKit' },
      ]
    : [
        { name: 'Stock Photos', id: 'stock' },
        { name: 'Image Library', id: 'library' },
      ]

  return (
    <div className={Styles.selectImageContainer}>
      <SegmentedControl
        variant="primary"
        isFullWidth
        rounded
        items={segmentedItems}
        activeItem={view}
        onItemClick={setView}
      >
        {(item) => <Item>{item.name}</Item>}
      </SegmentedControl>
      {view === 'stock' && <StockImages onMoreClick={() => handleUpload('stock')} />}
      {view === 'library' && <LibraryImages onMoreClick={() => handleUpload('uploads')} />}
      {view === 'brandKit' && (
        <BrandKitImages onMoreClick={() => handleUpload('brandKits', { brandKitId })} brandKitId={brandKitId} />
      )}
      <div className={Styles.actions}>
        <div className={Styles.action} onClick={() => fileInputRef?.click()}>
          <Upload5 />
          <Text variant="largeList">Upload Image</Text>
          <div className={Styles.chevron}>
            <ChevronRight size={16} color="var(--dark-40)" />
          </div>
        </div>
        <div className={Styles.action} onClick={() => handleUpload('ai')}>
          <AI />
          <Text variant="largeList"> Generate from AI</Text>
          <div className={Styles.chevron}>
            <ChevronRight size={16} color="var(--dark-40)" />
          </div>
        </div>
        <div className={Styles.action} onClick={() => handleUpload('link')}>
          <LinkAngled />
          <Text variant="largeList">Get image from a link</Text>
          <div className={Styles.chevron}>
            <ChevronRight size={16} color="var(--dark-40)" />
          </div>
        </div>
      </div>

      <VisuallyHidden>
        <input
          type="file"
          accept={accept}
          onChange={(e) => {
            const files = Array.from(e.target.files || [])
            handleOnChange(files)
          }}
          ref={setFileInputRef}
          multiple={false}
          name="insert image"
          id="insert image"
          data-testid="file-input"
        />
      </VisuallyHidden>
    </div>
  )
})
